import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useMyAnagrafica } from '../../hooks/useMyAnagrafica';
import { X, Trash2, ShoppingCart, Send, Package, Truck, ArrowLeft, Percent, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { getProductThumb } from '../../data/productImages';
import { useCartQuote } from '../../hooks/useCartQuote';
import { PendingPaymentBanner } from '../portal/PendingPaymentBanner';
import { QtyInput } from '../product/QtyInput';
import { articoliEPezzi } from '../../utils/articoliEPezzi';


function CartItemImage({ sku, category }: { sku: string; category: string }) {
  const [loaded, setLoaded] = useState(false);
  const src = getProductThumb(sku, category).replace('w=80', 'w=160');
  return (
    <div className="relative w-full h-full">
      {!loaded && <div className="absolute inset-0 bg-secondary/40 animate-pulse" />}
      <img
        src={src}
        alt={sku}
        className={`w-full h-full object-cover transition-opacity duration-150 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        loading="lazy"
      />
    </div>
  );
}

export function CartDrawer(): React.ReactElement | null {
  const { items, isOpen, setIsOpen, updateQty, removeItem, clearCart, totalItems } = useCart();
  const { user, hasRole, canPlaceOrder } = useAuth();
  // La soglia di spedizione gratuita è per-cliente (an_hhegimportominimo nel
  // gestionale, richiesta utente 24/02/2026) — nessun fallback globale: se il
  // cliente non ha questo valore impostato, non mostriamo alcun messaggio.
  // `enabled=false` per ruoli non-cliente (admin/agente/guest) evita una
  // chiamata 403 inutile dato che CartDrawer è montato globalmente.
  const isClientRole = hasRole('cliente', 'cliente_subordinato');
  const { data: myAnagrafica } = useMyAnagrafica(isClientRole);
  const freeShippingThreshold = myAnagrafica?.trasportoMinimo && myAnagrafica.trasportoMinimo > 0
    ? myAnagrafica.trasportoMinimo
    : null;
  // Riepilogo sconti — di default CHIUSO (richiesta utente 25/02/2026),
  // il cliente lo espande solo se vuole vedere il dettaglio riga per riga.
  const [scontiExpanded, setScontiExpanded] = useState(false);
  // Prezzi come li scriverà il gestionale (il gestionale comanda): listino
  // con gli sconti NTS, e lo sconto scatola sulle sole scatole intere.
  const quote = useCartQuote(items, isOpen && !!user);

  if (!isOpen) return null;

  // "Continua gli acquisti" riporta all'ultima pagina di ricerca/catalogo
  // visitata (con i filtri applicati), non a una categoria fissa
  // (richiesta utente 24/02/2026) — impostata da ProductSearchPage ad
  // ogni ricerca. Fallback al catalogo toner se l'utente non ha ancora
  // visitato una pagina di ricerca in questa sessione.
  const continueShoppingHref = sessionStorage.getItem('dtk_last_search_url') || '/prodotti/toner';

  return (
    <div className="fixed inset-0 z-[90] flex justify-end" data-testid="cart-drawer">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
      
      {/* Drawer */}
      <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-5 h-14 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-[hsl(214,58%,41%)]" />
            <h2 className="text-sm font-semibold">Carrello</h2>
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-[hsl(214,58%,41%)] text-white font-medium">{totalItems}</span>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-11 h-11 lg:w-8 lg:h-8 rounded-lg flex items-center justify-center hover:bg-secondary/60 transition-colors"
            data-testid="cart-close-button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Contenuto scrollabile: articoli + riepilogo sconti (spedizione,
            totali, tabella sconti, budget) — unificati in un'unica
            area di scroll cosi la tabella sconti non "spinge" via gli
            articoli né va in overlap col resto del drawer quando contiene
            molte righe (fix 21/02/2026, segnalato con tabella sconto
            scatola visibile). Solo i pulsanti azione restano fissi in
            fondo (vedi footer sotto). */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-secondary/60 flex items-center justify-center mb-4">
                <Package className="w-7 h-7 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold mb-1">Carrello vuoto</h3>
              <p className="text-xs text-muted-foreground mb-4">Aggiungi prodotti dal catalogo per richiedere un preventivo.</p>
              <button
                onClick={() => setIsOpen(false)}
                className="h-9 px-4 rounded-lg border text-sm font-medium hover:bg-secondary/60 transition-colors"
              >
                Continua a navigare
              </button>
            </div>
          ) : (
            <>
              <div className="px-5 pt-4 empty:hidden"><PendingPaymentBanner compact /></div>
              <div className="divide-y">
                {items.map(item => (
                  <div key={item.sku} className="px-5 py-4 flex gap-3" data-testid="cart-item">
                    <Link
                      to={`/prodotti/${item.category}/${item.sku}`}
                      onClick={() => setIsOpen(false)}
                      className="w-16 h-16 rounded-lg border overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-[hsl(var(--dtk-focus))]/30 transition-all"
                    >
                      <CartItemImage sku={item.sku} category={item.category} />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/prodotti/${item.category}/${item.sku}`}
                        onClick={() => setIsOpen(false)}
                        className="text-sm font-medium line-clamp-2 block hover:text-[hsl(214,58%,41%)] transition-colors"
                      >
                        {item.title}
                      </Link>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[13px] font-mono text-[hsl(214,58%,41%)]">{item.sku}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-secondary/60">{item.brand}</span>
                      </div>
                      {/* Line total */}
                      {(() => {
                        if (quote.nonVendibile(item)) {
                          return (
                            <div className="mt-1.5 text-[11px] font-semibold text-red-700" data-testid="cart-item-non-disponibile">
                              {quote.rimosso(item) ? 'Articolo non più disponibile' : 'Non disponibile per l’acquisto online'}
                            </div>
                          );
                        }
                        const r = quote.riga(item);
                        if (!r) return null;
                        return (
                          <div className="mt-1.5 text-[13px] lg:text-[10px] text-muted-foreground">
                            {item.qty} pz × € {r.prezzo.toFixed(2).replace('.', ',')}
                            {r.percentualeSconto > 0 && <span className="text-emerald-700 font-medium"> −{r.percentualeSconto.toLocaleString('it-IT')}%</span>}
                            {' '}= <strong className="text-foreground">€ {r.totaleLinea.toFixed(2).replace('.', ',')}</strong>
                          </div>
                        );
                      })()}
                      {/* Qty controls */}
                      <div className="flex items-center gap-2 mt-2">
                        <QtyInput
                          value={item.qty}
                          onChange={(q) => updateQty(item.sku, q)}
                          // Oltre la disponibilità: ``updateQty`` ferma la riga al massimo e lo dice.
                          onExceedMax={(q) => updateQty(item.sku, Math.min(q, 999))}
                          size="sm"
                          max={item.stock != null ? Math.min(Math.floor(item.stock), 999) : 999}
                        />
                        {!quote.nonVendibile(item) && item.stock != null && item.stock > 0 && item.qty > item.stock && (
                          <span className="text-[9px] text-amber-600 font-medium">
                            Solo {Math.floor(item.stock)} disp.
                          </span>
                        )}
                        <button
                          onClick={() => removeItem(item.sku)}
                          className="w-11 h-11 lg:w-7 lg:h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors"
                          data-testid="cart-remove-item"
                          aria-label={`Rimuovi ${item.title} dal carrello`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Riepilogo (spedizione, totali, sconti, budget) —
                  ora dentro l'area di scroll, non più un footer a
                  altezza fissa che comprimeva la lista articoli. */}
              {(() => {
                const cartTotal = quote.totaleListino;
                const scontiRows = items
                  .map(item => {
                    const r = quote.riga(item);
                    if (!r || r.percentualeSconto <= 0) return null;
                    return { sku: item.sku, title: item.title, pct: r.scatola?.sconto ?? r.percentualeSconto,
                             boxes: r.scatola?.scatole ?? null, savings: r.prezzo * item.qty - r.totaleLinea };
                  })
                  .filter((r): r is { sku: string; title: string; pct: number; boxes: number | null; savings: number } => r !== null);
                const totalSavings = scontiRows.reduce((sum, r) => sum + r.savings, 0);
                const totalAfterSavings = quote.imponibile;
                const remaining = freeShippingThreshold != null ? freeShippingThreshold - cartTotal : 0;
                const freeShipping = freeShippingThreshold != null && remaining <= 0;
                const progress = freeShippingThreshold != null ? Math.min(100, (cartTotal / freeShippingThreshold) * 100) : 0;

                return (
                  <div className="border-t px-5 py-4 space-y-3">
                    {/* Free shipping progress — solo se il cliente ha una soglia
                        impostata nel gestionale (per-cliente, nessun default globale) */}
                    {freeShippingThreshold != null && (
                      <div>
                        {freeShipping ? (
                          <div className="flex items-center gap-2 text-xs text-emerald-700 font-medium">
                            <Truck className="w-3.5 h-3.5" /> Spedizione gratuita!
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                              <span>Spedizione gratuita da {freeShippingThreshold}€</span>
                              <span>Mancano {remaining.toFixed(2).replace('.', ',')}€</span>
                            </div>
                            <div className="w-full h-1.5 rounded-full bg-secondary">
                              <div className="h-full rounded-full bg-[hsl(var(--dtk-focus))] transition-all" style={{ width: `${progress}%` }} />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {scontiRows.length > 0 ? 'Subtotale ante sconto' : `Articoli ${articoliEPezzi(items.length, totalItems)}`}
                      </span>
                      <div className="text-right">
                        <span className={scontiRows.length > 0 ? 'text-xs text-black' : 'font-bold text-base'}>
                          € {cartTotal.toFixed(2).replace('.', ',')}
                        </span>
                        <span className="text-[10px] text-muted-foreground block">IVA esclusa</span>
                      </div>
                    </div>

                    {scontiRows.length > 0 && (
                      <div className="-mx-5 border-t border-emerald-200 bg-emerald-50/60" data-testid="cart-sconti-summary">
                        <button
                          type="button"
                          onClick={() => setScontiExpanded(v => !v)}
                          className="w-full flex items-center justify-between gap-2 px-5 py-3 text-left hover:bg-emerald-100/40 transition-colors"
                          data-testid="cart-sconti-toggle"
                        >
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                              <Percent className="w-3 h-3" />
                            </span>
                            <span className="text-xs font-bold text-emerald-800 whitespace-nowrap">Sconti applicati</span>
                            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5 tabular-nums whitespace-nowrap">
                              &minus; &euro; {totalSavings.toFixed(2).replace('.', ',')}
                            </span>
                          </div>
                          {scontiExpanded ? <ChevronUp className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />}
                        </button>
                        {scontiExpanded && (
                          <div className="px-5 pb-3" data-testid="cart-sconti-detail">
                            <table className="w-full text-[13px] border-collapse">
                              <thead>
                                <tr className="border-b border-emerald-200" style={{ color: '#3a3a36' }}>
                                  <th className="text-left font-semibold pb-1.5 pr-1.5">Cod. DTK</th>
                                  <th className="text-left font-semibold pb-1.5 pr-1.5">Descrizione</th>
                                  <th className="text-right font-semibold pb-1.5 pr-1.5">Sconto</th>
                                  <th className="text-right font-semibold pb-1.5 pr-1.5">Scatole</th>
                                  <th className="text-right font-semibold pb-1.5">Sconto riga</th>
                                </tr>
                              </thead>
                              <tbody>
                                {scontiRows.map(row => (
                                  <tr key={row.sku} className="border-b border-dashed border-emerald-200/70 last:border-0" data-testid={`cart-sconti-row-${row.sku}`}>
                                    <td className="py-2 pr-1.5 font-mono font-semibold text-foreground whitespace-nowrap align-top">{row.sku}</td>
                                    <td className="py-2 pr-1.5 align-top">
                                      <div className="truncate max-w-[110px]" style={{ color: '#3a3a36' }} title={row.title}>{row.title}</div>
                                    </td>
                                    <td className="py-2 pr-1.5 text-right tabular-nums whitespace-nowrap align-top" style={{ color: '#3a3a36' }}>{row.pct.toLocaleString('it-IT')}%</td>
                                    <td className="py-2 pr-1.5 text-right tabular-nums whitespace-nowrap align-top" style={{ color: '#3a3a36' }}>{row.boxes ? `×${row.boxes}` : '—'}</td>
                                    <td className="py-2 text-right tabular-nums font-bold text-emerald-700 whitespace-nowrap align-top">
                                      − € {row.savings.toFixed(2).replace('.', ',')}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="flex items-center justify-between pt-2 mt-1 border-t border-emerald-200">
                              <span className="text-sm font-semibold text-emerald-900">Prezzo finale IVA esclusa</span>
                              <span className="font-bold text-emerald-700 tabular-nums text-xl" data-testid="cart-total-after-sconti">
                                € {totalAfterSavings.toFixed(2).replace('.', ',')}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* Footer azioni — sempre visibile e fisso in fondo, non scrolla
            con la lista/tabella sconti, cosi "Procedi al Checkout" resta
            sempre raggiungibile. */}
        {items.length > 0 && (
          <div className="border-t px-5 py-4 flex-shrink-0 space-y-2 bg-white">
            {/* Il cestino globale era un secondo pulsante solo-icona
                accanto a "Continua gli acquisti", indistinguibile dal
                cestino di riga e senza conferma: due azioni distruttive
                dalla stessa forma, la meno reversibile del flusso. Ora è
                un link testuale fuori dalla riga delle azioni primarie. */}
            <Link to={continueShoppingHref} onClick={() => setIsOpen(false)} className="tap w-full h-11 lg:h-10 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-secondary/60 transition-colors" data-testid="cart-continue-shopping">
              <ArrowLeft className="w-3.5 h-3.5" /> Continua gli acquisti
            </Link>

            {/* Un operatore senza il ruolo «Crea ordine» non deve essere
                mandato al checkout per scoprire lì che non può inviare:
                il carrello che prepara è comunque il lavoro utile, lo
                invia il titolare. */}
            {canPlaceOrder ? (
              <Link
                to="/area-clienti/checkout"
                onClick={() => setIsOpen(false)}
                className="w-full h-11 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[hsl(214,58%,46%)] transition-colors duration-150 active:scale-[0.99]"
                data-testid="cart-request-quote-button"
              >
                <Send className="w-4 h-4" />
                Procedi al Checkout
              </Link>
            ) : (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-start gap-2"
                data-testid="cart-operatore-blocked"
              >
                <Lock className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-[13px] leading-relaxed text-amber-900">
                  {user?.impersonatoDa
                    ? 'In impersonazione non si inviano ordini per conto del cliente. Il carrello resta salvato per lui.'
                    : <>Il tuo profilo può preparare il carrello ma non inviare l&apos;ordine.
                  Il carrello resta salvato per il titolare dell&apos;account.</>}
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                if (window.confirm(`Svuotare il carrello? ${items.length} ${items.length === 1 ? 'articolo verrà rimosso' : 'articoli verranno rimossi'}.`)) {
                  clearCart();
                  toast.success('Carrello svuotato');
                }
              }}
              data-testid="cart-clear"
              className="tap w-full h-11 lg:h-9 flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-red-600 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Svuota carrello
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
